import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { CLP, QUOTE_STATUS } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, StatusBadge, EmptyState } from "@/components/ui-kit";
import { Plus, ArrowUpRight, Trash2, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import QuoteForm from "@/components/QuoteForm";

export default function QuotesPage() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const navigate = useNavigate();

  const load = async () => { const { data } = await api.get("/quotes"); setItems(data); };
  useEffect(() => { load(); }, []);

  const filtered = filter ? items.filter((i) => i.status === filter) : items;
  useEffect(() => { setSelected(new Set()); }, [filter]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((q) => q.id)));
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !window.confirm(`¿Eliminar ${ids.length} cotización(es)?`)) return;
    const { data } = await api.post("/quotes/bulk-delete", { ids });
    toast.success(`${data.deleted} eliminada(s)`);
    setSelected(new Set()); load();
  };

  return (
    <PageContainer>
      <PageHeader
        kicker={`${items.length} cotizaciones`}
        title="Cotizaciones"
        subtitle="Gestión de cotizaciones por reparaciones detectadas en mantenciones."
        actions={
          <PrimaryButton data-testid="quotes-new-button" onClick={() => setShow(true)}>
            <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Nueva cotización
          </PrimaryButton>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("")} data-testid="quotes-filter-all" className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border ${!filter ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300 hover:border-[#0A0A0A]"}`}>
          Todas
        </button>
        {QUOTE_STATUS.map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)} data-testid={`quotes-filter-${s.value}`} className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border ${filter === s.value ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300 hover:border-[#0A0A0A]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Sin cotizaciones" description="Crea una nueva cotización desde aquí o directamente desde un servicio." />
      ) : (
        <div className="bg-white border border-neutral-200">
          {selected.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-[#0A0A0A] text-white">
              <div className="font-mono-kpi text-xs uppercase tracking-[0.15em]">{selected.size} seleccionado(s)</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelected(new Set())} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:text-white px-3 py-1.5">Deseleccionar</button>
                <button data-testid="quotes-bulk-delete" onClick={bulkDelete} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] bg-[#D50000] hover:bg-[#B71C1C] text-white px-3 py-1.5"><Trash2 className="w-3 h-3 inline mr-1" />Eliminar</button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-[40px_2fr_1.5fr_1fr_120px_100px_40px] gap-3 px-5 py-3 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
            <button onClick={toggleAll} data-testid="quotes-select-all" className="flex items-center justify-center text-neutral-500 hover:text-[#FF5A00]">
              {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare className="w-4 h-4 text-[#FF5A00]" /> : <Square className="w-4 h-4" />}
            </button>
            <div>Título</div><div>Cliente</div><div>Número</div><div className="text-right">Total</div><div>Estado</div><div />
          </div>
          {filtered.map((q) => {
            const isChecked = selected.has(q.id);
            return (
              <div
                key={q.id}
                data-testid={`quote-row-${q.id}`}
                onClick={() => navigate(`/admin/cotizaciones/${q.id}`)}
                className={`grid grid-cols-[40px_2fr_1.5fr_1fr_120px_100px_40px] gap-3 px-5 py-3 border-b border-neutral-100 hover:bg-neutral-50 items-center text-sm cursor-pointer ${isChecked ? "bg-[#FF5A00]/5" : ""}`}
              >
                <button data-testid={`quote-select-${q.id}`} onClick={(e) => { e.stopPropagation(); toggleOne(q.id); }} className="flex items-center justify-center text-neutral-500 hover:text-[#FF5A00]">
                  {isChecked ? <CheckSquare className="w-4 h-4 text-[#FF5A00]" /> : <Square className="w-4 h-4" />}
                </button>
                <div className="font-medium truncate">{q.title}</div>
                <div className="text-neutral-600 truncate">{q.customer_name}</div>
                <div className="font-mono-kpi text-xs text-neutral-500">{q.number}</div>
                <div className="font-mono-kpi font-bold text-right">{CLP(q.total)}</div>
                <div><StatusBadge status={q.status} /></div>
                <ArrowUpRight className="w-3.5 h-3.5 text-neutral-400" />
              </div>
            );
          })}
        </div>
      )}

      {show && <QuoteForm onClose={() => setShow(false)} onCreated={(q) => { setShow(false); navigate(`/admin/cotizaciones/${q.id}`); }} />}
    </PageContainer>
  );
}
